import type { Many, ValueIteratee } from 'types/utility';

/* eslint-disable no-restricted-imports */
import {
  groupBy as lodashGroupBy,
  isInteger as lodashIsInteger,
  iteratee as lodashIteratee,
  keyBy as lodashKeyBy,
  orderBy as lodashOrderBy,
  sortBy as lodashSortBy,
  unionBy as lodashUnionBy,
  uniqBy as lodashUniqBy,
} from 'lodash-es';
import type * as z from 'zod';
/* eslint-enable no-restricted-imports */

export function notNull<T extends string | number | boolean | object>(
  item: T | null | undefined
): item is T {
  return item != null;
}

/**
 * Makes a version of a function where the first argument can be null|undefined,
 * in which case the return value will be undefined.
 * @param fn The original function, where the first argument is not optional
 */
export function makeFirstArgumentOptional<TFirst, TRest extends Array<unknown>, TReturn>(
  fn: (first: TFirst, ...rest: TRest) => TReturn
) {
  return (first: TFirst | null | undefined, ...rest: TRest): TReturn | undefined => {
    return first != null ? fn(first, ...rest) : undefined;
  };
}

/** Like lodash.isInteger, but type refining */
export function isInteger(x: any): x is number {
  return lodashIsInteger(x);
}

export function groupBy<T extends {}>(
  collection: ReadonlyArray<T> | Record<any, T> | null | undefined,
  iteratee: ValueIteratee<T>
): Record<string, Array<T>> {
  return lodashGroupBy(collection, iteratee);
}

export function groupByKeys<T extends {}>(
  collection: Array<T>,
  iteratee: ValueIteratee<T>
): Record<string, Array<T>> {
  const grouped: Record<string, Array<T>> = {};
  collection.forEach(item => {
    const value = lodashIteratee(iteratee)(item);
    const keys = Array.isArray(value) ? value.map(String) : [String(value)];
    keys.forEach(keyItem => {
      grouped[keyItem] ??= [];
      grouped[keyItem].push(item);
    });
  });
  return grouped;
}

export function sortBy<T extends {}>(
  collection: ReadonlyArray<T> | null | undefined,
  ...iteratees: Array<Many<ValueIteratee<T>>>
): Array<T> {
  return lodashSortBy(collection, ...iteratees);
}

export function keyBy<T extends {}>(
  collection: ReadonlyArray<T> | null | undefined,
  iteratee: ValueIteratee<T>
): Record<string, T> {
  return lodashKeyBy(collection, iteratee as any);
}

export function orderBy<T extends {}>(
  collection: ReadonlyArray<T> | null | undefined,
  iteratees: Many<ValueIteratee<T>>,
  orders?: Many<boolean | 'asc' | 'desc'>
): Array<T> {
  return lodashOrderBy(collection, iteratees, orders);
}

export function uniqBy<T extends {}>(
  array: ReadonlyArray<T> | null | undefined,
  iteratee: ValueIteratee<T>
): Array<T> {
  return lodashUniqBy(array, iteratee);
}

export function unionBy<T extends {}>(
  array1: ReadonlyArray<T> | null | undefined,
  array2: ReadonlyArray<T> | null | undefined,
  iteratee: ValueIteratee<T>
): Array<T> {
  return lodashUnionBy(array1, array2, iteratee);
}

export function pick<T, TKeys extends keyof T>(object: T, ...keys: Array<TKeys>): Pick<T, TKeys>;
export function pick<T, TKeys extends keyof T>(
  object: T | null | undefined,
  ...keys: Array<TKeys>
): Partial<Pick<T, TKeys>>;
export function pick(object: any, ...keys: Array<string>): any {
  return Object.fromEntries(
    Object.entries(object ?? {}).filter(([key]) => keys.includes(key as any))
  ) as any;
}

export function parseZodType<TType extends z.ZodType<any>>(
  type: TType,
  value: any
): z.infer<TType> {
  if (process.env.NODE_ENV === 'development') {
    return type.parse(value);
  }
  // Don't do anything in production
  return value;
}

export function objectNumberKeys(record: Record<number, unknown>): Array<number> {
  return Object.keys(record).map(key => parseInt(key, 10));
}

export function objectUnionKeys<TKey extends string>(record: Record<TKey, unknown>): Array<TKey> {
  return Object.keys(record) as Array<TKey>;
}

export const objectKeys = <Obj extends object>(obj: Obj): (keyof Obj)[] => {
  return Object.keys(obj) as (keyof Obj)[];
};

export function parseEnumValue<TEnum extends string>(
  value: unknown,
  enumValues: ReadonlyArray<TEnum>
): TEnum | undefined {
  return enumValues.find(v => v === value);
}
